/** @jsxImportSource react */
/**
 * React Context Provider for Memory Intelligence
 */

import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from "react";
import { MemoryIntelligenceClient } from "../../core/client.js";
import { MemoryIntelligenceConfig } from "../../core/types.js";

interface MemoryIntelligenceContextValue {
  client: MemoryIntelligenceClient;
}

const MemoryIntelligenceContext =
  createContext<MemoryIntelligenceContextValue | null>(null);

interface MemoryIntelligenceProviderProps {
  config: MemoryIntelligenceConfig;
  children: ReactNode;
}

export function MemoryIntelligenceProvider({
  config,
  children,
}: MemoryIntelligenceProviderProps) {
  // Use useState to avoid recreating client on every render when config object is recreated
  const [client, setClient] = useState(
    () => new MemoryIntelligenceClient(config),
  );

  // Only recreate client when primitive config values change
  useEffect(() => {
    setClient(new MemoryIntelligenceClient(config));
  }, [
    config.apiUrl,
    config.apiKey,
    config.authToken,
    config.authType,
    config.allowMissingAuth,
    config.timeout,
    config.processingMode,
    config.enableCache,
    config.cacheTTL,
  ]);

  return (
    <MemoryIntelligenceContext.Provider value={{ client }}>
      {children}
    </MemoryIntelligenceContext.Provider>
  );
}

export function useMemoryIntelligenceContext() {
  const context = useContext(MemoryIntelligenceContext);
  if (!context) {
    throw new Error(
      "useMemoryIntelligenceContext must be used within MemoryIntelligenceProvider",
    );
  }
  return context;
}

export { MemoryIntelligenceContext };
